import { createContext } from "react";
import { CodedProperty, Optional, ValidationFailure } from "sprint-common-web";

import { CreateEntityResponse } from "../../../../../common/api/common/CreateEntityResponse";
import { CrossTypeContentSearchItem } from "../../../../../common/components/form/CrossTypeContentSearch";
import { ContentSubtype } from "../../../../../common/types/ContentSubtype";
import { DeliveryEndpointReference } from "../../../../../common/types/DeliveryEndpoint";
import { DeliveryEndpointType } from "../../../../../common/types/DeliveryEndpointType";
import { EncodeSpecification } from "../../../../../common/types/EncodeSpecification";
import { Integrator } from "../../../../../common/types/Integrator";
import { System } from "../../../../../common/types/System";
import { OrdersAirline } from "../../../../common/types/OrdersAirline";
import { Resolution } from "../../../../common/types/Resolution";
import {
    OrderAncillaryVersionItem,
    OrderContentVersionItem,
    OrderEpisodeVersionItem,
    OrderMovieVersionItem
} from "../../types/OrderContentVersionItem";
import { OrderMuxSpecificationItem } from "../../types/OrderMuxSpecificationItem";
import { OrderSoundtrackItem } from "../../types/OrderSoundtrackItem";
import { OriginSubtitlesFileName } from "../subtitles/subtitles";

export interface ValidateCreateOrderResult {
    isValid: boolean;
    errors: ValidationFailure[];
    warnings: ValidationFailure[];
}

export interface CreateOrderArgs {
    dueDate: string;
    targetFileName: string;
    pONumber: string;
    startDate: string;
    deliveryFolder?: string;
    subtitlesTargetFileName?: string;
    isSubtitlesEmbedded: boolean;
    originSubtitlesFileNames?: Array<OriginSubtitlesFileName>;
}

export interface ICreateOrderContext {
    contentType: ContentSubtype | undefined;
    contentDetails: Optional<CrossTypeContentSearchItem>;
    contentVersion: OrderContentVersionItem | undefined;
    airline: OrdersAirline | undefined;
    system: System | undefined;
    integrator: Integrator | undefined;
    deliveryType: DeliveryEndpointType | undefined;
    deliveryEndpoint: DeliveryEndpointReference | undefined;
    encodeSpecification: EncodeSpecification | undefined;
    muxSpecification: OrderMuxSpecificationItem | undefined;
    soundtracks: OrderSoundtrackItem[];
    embeddedSubtitle: Optional<CodedProperty>;
    dynamicSubtitles: CodedProperty[];
    targetResolution: Optional<Resolution>;
    targetFileName: Optional<string>;
    originSubtitlesFileNames: Optional<OriginSubtitlesFileName[]>;
    hasAirlineMismatch: boolean;
    setContentType(contentSubtype: ContentSubtype | undefined): void;
    setContentDetails(contentDetails: Optional<CrossTypeContentSearchItem>): void;
    setMovieVersion(version: OrderMovieVersionItem | undefined): void;
    setEpisodeVersion(version: OrderEpisodeVersionItem | undefined): void;
    setAncillaryVersion(version: OrderAncillaryVersionItem | undefined): void;
    setAirline(airline: OrdersAirline | undefined): void;
    setSystem(system: System | undefined): void;
    setEncodeSpecification(encodeSpecification: EncodeSpecification | undefined): void;
    setSoundtracks(value: SetSoundtracksAction): void;
    setEmbeddedSubtitle(value: CodedProperty): void;
    setDynamicSubtitles(value: SetSubtitlesAction): void;
    setTargetResolution(res: Resolution | undefined): void;
    clearContentVersion(): void;
    createNewOrder(args: CreateOrderArgs): Promise<CreateEntityResponse>;
    setOriginSubtitlesFileNames: (value: Optional<OriginSubtitlesFileName[]>) => void;
    setHasAirlineMismatch: (value: boolean) => void;
    setTargetFileName: (value?: string) => void;
}

const defaultCreateOrderContext: ICreateOrderContext = {} as ICreateOrderContext;

export const CreateOrderContext = createContext<ICreateOrderContext>(defaultCreateOrderContext);

type SetSoundtracksAction = OrderSoundtrackItem[] | ((prevState: OrderSoundtrackItem[]) => OrderSoundtrackItem[]);
type SetSubtitlesAction = CodedProperty[] | ((prevState: CodedProperty[]) => CodedProperty[]);
